/**
 * Admin Panel - System Health: cron execution schemas.
 *
 * The bodies of the cron-jobs, execution-history and failure-rate endpoints.
 * `ZAdminHealth` (`../zadmin-health.ts`) is where they are bound to routes, and
 * re-exports them.
 */

import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";

/**
 * Cron execution status.
 *
 * Hand-written rather than derived from `DbEnum.CronExecutionStatus`, so it
 * does NOT widen when the Prisma enum does. A value the database can write and
 * this schema does not list is stripped or rejected on the way out, turning a
 * new status into a runtime response failure rather than a compile error —
 * which is exactly what adding `ABANDONED` would have done. Keep the two in
 * step by hand.
 *
 * ABANDONED: the process that wrote this row's STARTED never recorded a
 * handler outcome. Either that pod closed the row itself on its way out — the
 * run was still going at its shutdown's settle bound — or, when the pod died
 * without a graceful shutdown, a stale-execution sweep closed it later.
 * Distinct from FAILED, which means the run faulted — the handler threw, or
 * the database refused its STARTED row so it never ran — conflating
 * them makes every rate over this table a deploy meter.
 */
export const CronExecutionStatusSchema = z.enum([
  "STARTED",
  "SUCCESS",
  "FAILED",
  "SKIPPED",
  "ABANDONED"
]);

/**
 * Cron execution log entry
 */
export const CronExecutionLogSchema = z.object({
  id: z.string(),
  cronKey: z.nativeEnum(DbEnum.CronKey),
  jobTitle: z.string().nullable(),
  status: CronExecutionStatusSchema,
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  durationMs: z.number().nullable(),
  lockAcquired: z.boolean(),
  lockSkippedReason: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  errorMessage: z.string().nullable(),
  errorStack: z.string().nullable(),
  instanceHostname: z.string().nullable(),
  instanceType: z.string().nullable(),
  instancePid: z.number().nullable(),
  instanceEnv: z.string().nullable(),
  triggeredManually: z.boolean(),
  triggeredByUserEmail: z.string().nullable()
});

/**
 * Cron job summary with statistics
 */
export const CronJobSummarySchema = z.object({
  cronKey: z.nativeEnum(DbEnum.CronKey),
  jobTitle: z.string().nullable(),
  schedule: z.string(),
  isEnabled: z.boolean(),
  supportsManualTrigger: z.boolean(),
  lastExecution: CronExecutionLogSchema.nullable(),
  stats: z.object({
    /**
     * The share of ticks that COMPLETED THE WORK, as a percentage:
     * `successful / (successful + recentFailures + recentAbandoned)`.
     *
     * A reap counts AGAINST this rate. That is not a re-merge of the
     * FAILED/ABANDONED split — the two remain separate fields below, and a
     * reap is still not a fault. It is the narrower claim that a tick whose
     * pod vanished did not get the work done either. Leaving reaps out of the
     * denominator let a cron shedding 38 of 100 ticks report 100%.
     *
     * For "did the handler throw", read `recentFailures`, or
     * `GET /api/admin/health/cron-failure-rates`, whose `failureRatePct` is
     * the fault view and excludes ABANDONED from both of its terms.
     *
     * SKIPPED is excluded — another instance held the lock and ran that tick.
     * STARTED is excluded — a run in flight has no verdict yet.
     *
     * ⚠️ `0` when no run reached a verdict, which reads identically to a cron
     * that failed every tick. `CronFailureRateSchema.failureRatePct` reports
     * `null` there instead; this field is not nullable and does not.
     */
    successRate: z.number(),
    avgDurationMs: z.number().nullable(),
    totalRuns: z.number(),
    /**
     * Runs that faulted: the handler threw, or the database refused the start
     * row so the handler never ran. Excludes reaps — see `recentAbandoned`.
     */
    recentFailures: z.number(),
    /**
     * Runs closed ABANDONED because the process that opened them did not close
     * them — by that pod's own shutdown when the run outlived its settle bound,
     * or by the stale-execution sweep. Surfaced rather than dropped, so a fleet
     * losing pods still reads as one; `recentFailures + recentAbandoned` is the
     * old, conflated number.
     */
    recentAbandoned: z.number(),
    /**
     * Runs still STARTED past the stale floor whose cron lock no longer names
     * them: their process is presumed dead and no sweep has closed them yet.
     * STARTED has no verdict, so no rate counts it — this field is what stops
     * a cron whose pod dies under every run from reading "0 failures".
     */
    recentStaleStarted: z.number()
  })
});

/**
 * Paginated execution history
 */
export const CronExecutionHistorySchema = z.object({
  executions: z.array(CronExecutionLogSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number()
});

/**
 * FAILED / (FAILED + SUCCESS) for one cron key over a trailing window.
 *
 * `failureRatePct` is `null`, never `0`, when `totalConclusive` is 0 — a
 * cron with no FAILED/SUCCESS rows in the window has no rate to report,
 * and reporting `0` there would read as an observed-healthy cron rather
 * than as one with nothing conclusive to observe.
 *
 * ABANDONED rows are in neither the numerator nor the denominator; they are
 * reported on their own field. A reap means the pod carrying the run was
 * replaced — expected control flow, not a fault — and counting it as FAILED
 * made this metric a deploy meter rather than a health signal.
 */
export const CronFailureRateSchema = z.object({
  cronKey: z.nativeEnum(DbEnum.CronKey),
  jobTitle: z.string().nullable(),
  /**
   * Rows that faulted: the handler threw, or the database refused the start row
   * so the handler never ran. Excludes reaps — see `abandoned`.
   */
  failed: z.number(),
  success: z.number(),
  /**
   * Rows closed out because the process that opened them never recorded an
   * outcome — by that pod's own shutdown, or later by the stale-execution
   * sweep. `failed + abandoned` recovers the pre-split number.
   *
   * ⚠️ Counts the ABANDONED status only. Reaps written before that status
   * existed remain FAILED and are not backfilled, so over a window reaching
   * back past that deploy this undercounts and `failed` overcounts.
   */
  abandoned: z.number(),
  /**
   * Rows still STARTED past the stale floor whose cron lock no longer names
   * them — presumed dead, not yet swept. Beside `failed` so "0 failed" cannot
   * hide a cron whose pod dies under it.
   */
  staleStarted: z.number(),
  /** `failed + success` — excludes SKIPPED, STARTED and ABANDONED rows. */
  totalConclusive: z.number(),
  failureRatePct: z.number().nullable()
});
